
import { cn } from "@/lib/utils";
import { Home, Inbox, Heart, User, Menu, Utensils, ChefHat, BarChart, ClipboardList } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { CooksyLogo } from "@/components/CooksyLogo";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const foodieNavItems = [
  { icon: Home, label: "Home", path: "/" },
  { icon: Utensils, label: "My Meals", path: "/meals" },
  { icon: Inbox, label: "Messages", path: "/inbox", showUnreadCount: true },
  { icon: Heart, label: "Favorites", path: "/favorites" },
  { icon: User, label: "Profile", path: "/profile" },
];

const chefNavItems = [
  { icon: ChefHat, label: "My Meals", path: "/seller/meals" },
  { icon: ClipboardList, label: "Orders", path: "/seller/orders" },
  { icon: BarChart, label: "Business", path: "/seller/business" },
  { icon: Inbox, label: "Inbox", path: "/seller/inbox", showUnreadCount: true },
  { icon: User, label: "Profile", path: "/seller/profile" },
];

export const SideNav = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const isSellerMode = location.pathname.startsWith('/seller');
  
  const navItems = isSellerMode ? chefNavItems : foodieNavItems;

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["unread-messages", isSellerMode],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return 0;

        const { data, error } = await supabase
          .from("conversations")
          .select("unread_count")
          .eq(isSellerMode ? "cook_id" : "foodie_id", user.id);

        if (error) {
          console.error("Error fetching unread count:", error);
          return 0;
        }

        // Calculate total unread messages
        const totalUnread = data.reduce((sum, conv) => {
          // Ensure unread_count is treated as a number (could be null)
          const count = conv.unread_count !== null ? Number(conv.unread_count) : 0;
          return sum + count;
        }, 0);
        
        console.log(`SideNav unread count for ${isSellerMode ? 'seller' : 'foodie'}: ${totalUnread}`);
        return totalUnread;
      } catch (err) {
        console.error("Error in SideNav unread count:", err);
        return 0;
      }
    },
    refetchInterval: 3000, // More frequent refresh
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  });

  return (
    <div className={cn(
      "hidden md:flex flex-col border-r border-border h-[100dvh] sticky inset-0 bg-background transition-all duration-300",
      isCollapsed ? "w-[70px]" : "w-[240px]"
    )}>
      <div className="p-4 flex items-center justify-between border-b">
        <div className={cn("transition-all duration-300", 
          isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
        )}>
          <CooksyLogo />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="shrink-0"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      <nav className="flex-1 p-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground",
                isCollapsed && "justify-center"
              )}
            >
              <div className="relative">
                <Icon className="h-5 w-5 shrink-0" />
                {item.showUnreadCount && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </div>
              {!isCollapsed && (
                <>
                  <span>{item.label}</span>
                  {item.showUnreadCount && unreadCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};
